import React, { useContext, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import AddBossMode from './components/AddBossMode';
import BossModeTable from './components/BossModeTable';
import useRefetch from 'hooks/useRefetch';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import useBossModeAccountsFilters from './hooks/useBossModeAccountsFilters';
import MultiFilterPanel from 'components/MultiFilterPanel';
import MultiSortPanel from 'components/MultiSortPanel';
import useSearchParams from 'hooks/useSearchParams';
import { getHasActiveFilters, getIsArrayEmpty } from 'utils';
import useTableFetchState from 'hooks/useTableFetchState';
import { User, TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import ManageColumns from 'components/ManageColumns';
import {
  HEADING_TRANSLATIONS,
  TABLE_COLUMNS,
  LOCKED_COLUMN,
} from './components/BossModeTable/components/data';

import './BossModeAccounts.styles.scss';

type BossModeAccountsProps = {
  className?: string;
};

const BossModeAccounts: React.FC<BossModeAccountsProps> = (props) => {
  const { className } = props;

  const classes = classNames('boss-mode-accounts', className);

  const { refetchFlag, refetch } = useRefetch();

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const { t } = useTranslation();

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  const {
    filterOptions,
    translatedSortOptions,
    multiSortDirectionTranslation,
    columnFilterOptions,
  } = useBossModeAccountsFilters();

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onReorder,
    onColumnSizingChange,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(
    TABLES_WITH_COLUMN_SETTINGS.BOSS_MODE_LIST,
    TABLE_COLUMNS,
  );

  const {
    isLoading,
    totalPages,
    totalItems,
    data: bossModeAccounts,
    setIsLoading,
    setPaginatedData,
  } = useTableFetchState<User>();

  const { popServerError } = useContext(PopNotificationsContext);

  const searchParams = useSearchParams();

  const isEmpty = !isLoading && getIsArrayEmpty(bossModeAccounts);

  const hasActiveFilters = useMemo(
    () => getHasActiveFilters(filterOptions, searchParams),
    [filterOptions, searchParams],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const { data } = await api.portfolio.admin.getBossModeAccounts({
          $limit: limit,
          ...searchParams,
        });

        setPaginatedData(data);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    searchParams,
    refetchFlag,
    limit,
    popServerError,
    setPaginatedData,
    setIsLoading,
  ]);

  return (
    <div className={classes}>
      <div className="admin-dashboard-router__heading">
        <h1>{t('bossModeAccountsList')}</h1>

        <div className="boss-mode-accounts__filters">
          {(!isEmpty || hasActiveFilters) && (
            <>
              {!isEmpty && (
                <NumberOfResultsPerPageFilter
                  limit={limit}
                  updateLimit={updateLimit}
                />
              )}
              <MultiFilterPanel ref={multiFilterRef} filters={filterOptions} />
              <MultiSortPanel
                ref={multiSortRef}
                options={translatedSortOptions}
                sortDirectionTranslations={multiSortDirectionTranslation}
              />
            </>
          )}
          <ManageColumns
            allColumns={allColumns}
            hiddenColumns={hiddenColumns}
            headingTranslations={HEADING_TRANSLATIONS}
            lockedColumn={LOCKED_COLUMN}
            isDefaultColumns={isDefaultColumns}
            onSave={saveColumnSettings}
            onResetToDefault={resetToDefault}
          />
          <AddBossMode onAddBossMode={refetch} />
        </div>
      </div>

      <ActiveFilterSortPills
        filterOptions={filterOptions}
        sortOptions={translatedSortOptions}
        onChipLabelClick={onChipLabelClick}
        onSortChipLabelClick={onSortChipLabelClick}
      />

      <BossModeTable
        bossModeAccounts={bossModeAccounts}
        isLoading={isLoading}
        totalPages={totalPages}
        totalItems={totalItems}
        columnFilterOptions={columnFilterOptions}
        refetch={refetch}
        columns={columns}
        columnSizing={columnSizing}
        onReorder={onReorder}
        onColumnSizingChange={onColumnSizingChange}
      />
    </div>
  );
};

export default BossModeAccounts;
